/**
 * EditModal - Unified Edit Modal Component
 *
 * A modern, consistent modal for editing records in a popup dialog.
 * Features:
 * - Tab-based navigation within modal
 * - Breadcrumb navigation
 * - Status indicator
 * - Floating footer with save/cancel actions
 * - Keyboard shortcuts (Cmd/Ctrl+S to save)
 * - Unsaved changes detection
 * - Smooth animations
 */

import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/base';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
	CheckCircle2,
	AlertCircle,
	Clock,
	ChevronRight,
	Loader2,
	Save,
	X,
} from 'lucide-react';

export interface IFormSection {
	id: string;
	label: string;
	icon?: React.ReactNode;
	badge?: string | number;
	badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success';
	content: React.ReactNode;
	errorCount?: number;
}

export interface IEditModalProps {
	/** Whether the modal is open */
	isOpen: boolean;

	/** Callback when modal requests to close */
	onClose: () => void;

	/** Modal title */
	title: string;

	/** Description text below title */
	description?: string;

	/** Modal size */
	sizeClass?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

	/** Form sections/tabs */
	tabs?: IFormSection[];

	/** Single form content (if no tabs) */
	children?: React.ReactNode;

	/** Breadcrumb items */
	breadcrumbs?: {
		label: string;
		href?: string;
	}[];

	/** Current status of the record */
	status?: string;
	statusVariant?: 'default' | 'secondary' | 'destructive' | 'success' | 'warning';

	/** Last modified timestamp */
	lastModified?: Date | string;

	/** Whether there are unsaved changes */
	hasUnsavedChanges?: boolean;

	/** Loading state */
	isLoading?: boolean;

	/** Saving state */
	isSaving?: boolean;

	/** Save callback */
	onSave?: () => void;

	/** Cancel callback */
	onCancel?: () => void;

	/** Additional footer actions */
	footerActions?: React.ReactNode;

	/** Keyboard shortcut hint */
	saveShortcut?: string;
}

const sizeToClass = (sizeClass?: string) => {
	switch (sizeClass) {
		case 'sm': return 'max-w-sm';
		case 'md': return 'max-w-md';
		case 'lg': return 'max-w-lg';
		case 'xl': return 'max-w-xl';
		case '2xl': return 'max-w-2xl';
		case 'full': return 'max-w-[96vw]';
		default: return 'max-w-lg';
	}
};

export const EditModal = ({
	isOpen,
	onClose,
	title,
	description,
	sizeClass = 'lg',
	tabs,
	children,
	breadcrumbs,
	status,
	statusVariant = 'secondary',
	lastModified,
	hasUnsavedChanges = false,
	isLoading = false,
	isSaving = false,
	onSave,
	onCancel,
	footerActions,
	saveShortcut = '⌘S',
}: IEditModalProps) => {
	const [activeTab, setActiveTab] = useState(tabs?.[0]?.id || '');
	const [isClosing, setIsClosing] = useState(false);
	const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

	// Calculate active tab index
	const activeTabIndex = useMemo(() => {
		return tabs?.findIndex((tab) => tab.id === activeTab) ?? -1;
	}, [tabs, activeTab]);

	// Format last modified time
	const formattedLastModified = useMemo(() => {
		if (!lastModified) return null;
		const date = typeof lastModified === 'string' ? new Date(lastModified) : lastModified;
		return new Intl.DateTimeFormat('vi-VN', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		}).format(date);
	}, [lastModified]);

	// Handle close with animation
	const handleClose = useCallback(() => {
		if (hasUnsavedChanges && !isSaving) {
			const allowClose = window.confirm('Bạn có thay đổi chưa lưu. Đóng cửa sổ và bỏ thay đổi?');
			if (!allowClose) return;
		}
		setIsClosing(true);
		setTimeout(() => {
			setIsClosing(false);
			onClose();
		}, 150);
	}, [hasUnsavedChanges, isSaving, onClose]);

	// Keyboard shortcut handler
	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === 's') {
				e.preventDefault();
				if (onSave && !isSaving && !isLoading) {
					onSave();
				}
			}
			if (e.key === 'Escape') {
				handleClose();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, onSave, isSaving, isLoading, handleClose]);

	// Scroll active tab into view
	useEffect(() => {
		const activeButton = tabRefs.current.get(activeTab);
		activeButton?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
	}, [activeTab]);

	// Reset active tab when modal opens
	useEffect(() => {
		if (isOpen && tabs?.length) {
			setActiveTab(tabs[0].id);
		}
	}, [isOpen, tabs]);

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent
				className={cn(
					'w-[calc(100vw-1rem)] sm:w-[calc(100vw-3rem)] md:w-full',
					sizeToClass(sizeClass),
					'max-h-[90vh] flex flex-col gap-0 overflow-hidden rounded-2xl border border-border/70 p-0 shadow-2xl',
					'overflow-hidden rounded-lg',
					'ux-modal-enter',
					isClosing && 'animate-out fade-out slide-out-to-bottom-2 duration-150'
				)}
			>
				{/* Modal Header */}
				<DialogHeader className='border-b bg-muted/20 px-4 py-3 sm:px-6 shrink-0'>
					{/* Breadcrumb */}
					{breadcrumbs && breadcrumbs.length > 0 && (
						<div className='mb-2'>
							<nav aria-label='breadcrumb'>
								<ol className='flex flex-wrap items-center gap-1 text-xs text-muted-foreground'>
									{breadcrumbs.map((crumb, index) => (
										<li key={index} className='flex items-center gap-1'>
											{crumb.href ? (
												<a
													href={crumb.href}
													className='text-muted-foreground hover:text-foreground transition-colors'
												>
													{crumb.label}
												</a>
											) : (
												<span className={cn(
													'text-xs',
													index === breadcrumbs.length - 1 ? 'font-medium text-foreground' : 'text-muted-foreground'
												)}>
													{crumb.label}
												</span>
											)}
											{index < breadcrumbs.length - 1 && (
												<ChevronRight className='h-3 w-3 text-muted-foreground/50' />
											)}
										</li>
									))}
								</ol>
							</nav>
						</div>
					)}

					{/* Title Row */}
					<div className='flex items-center justify-between gap-4'>
						<div className='flex items-center gap-3 min-w-0'>
							<DialogTitle className='text-lg font-semibold truncate'>{title}</DialogTitle>
							{status && (
								<Badge variant={statusVariant} className='shrink-0'>
									{status}
								</Badge>
							)}
						</div>
						<button
							type='button'
							onClick={handleClose}
							aria-label='Đóng hộp thoại chỉnh sửa'
							className='shrink-0 p-1.5 rounded-md hover:bg-muted transition-colors cursor-pointer'
						>
							<X className='h-4 w-4 text-muted-foreground' />
						</button>
					</div>

					{/* Description & Meta */}
					<div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1'>
						{description && <span>{description}</span>}
						{formattedLastModified && (
							<span className='inline-flex items-center gap-1'>
								<Clock className='h-3 w-3' />
								{formattedLastModified}
							</span>
						)}
						{hasUnsavedChanges && (
							<span className='inline-flex items-center gap-1 text-amber-600 dark:text-amber-500'>
								<AlertCircle className='h-3 w-3' />
								Chưa lưu
							</span>
						)}
					</div>
				</DialogHeader>

				{/* Tab Navigation */}
				{tabs && tabs.length > 0 && (
					<div className='border-b bg-background shrink-0'>
						<div className='relative'>
							<div className='flex items-center overflow-x-auto scrollbar-hide px-4 sm:px-6'>
								{tabs.map((tab) => (
									<button
										key={tab.id}
										ref={(el) => {
											if (el) tabRefs.current.set(tab.id, el);
										}}
										type='button'
										onClick={() => setActiveTab(tab.id)}
										className={cn(
											'relative flex items-center gap-2 px-3 py-2.5 text-sm font-medium',
											'border-b-2 rounded-none whitespace-nowrap shrink-0',
											'transition-all duration-200',
											activeTab === tab.id
												? 'border-primary text-foreground'
												: 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30',
											tab.errorCount && tab.errorCount > 0 && 'text-destructive border-destructive'
										)}
									>
										{tab.icon && (
											<span className={cn(
												'transition-transform duration-200',
												activeTab === tab.id && 'scale-110'
											)}>
												{tab.icon}
											</span>
										)}
										<span>{tab.label}</span>
										{tab.badge !== undefined && (
											<Badge
												variant={tab.errorCount && tab.errorCount > 0 ? 'destructive' : (tab.badgeVariant || 'secondary')}
												className={cn(
													'text-xs px-1.5 py-0 min-w-[1.1rem] h-4 justify-center',
													tab.errorCount && tab.errorCount > 0 && 'animate-pulse'
												)}
											>
												{tab.badge}
											</Badge>
										)}
									</button>
								))}
							</div>

							{/* Progress Indicator */}
							{tabs.length > 1 && (
								<div className='absolute bottom-0 left-0 right-0 h-0.5 bg-muted/50'>
									<div
										className='h-full bg-primary transition-all duration-300 ease-out'
										style={{
											width: `${((activeTabIndex + 1) / tabs.length) * 100}%`,
										}}
									/>
								</div>
							)}
						</div>
					</div>
				)}

				{/* Modal Body */}
				<div className='flex-1 overflow-y-auto'>
					{tabs && tabs.length > 0 ? (
						<div className='p-4 sm:p-6'>
							{tabs.map((tab) => (
								<div
									key={tab.id}
									className={cn(
										'animate-in fade-in-0 slide-in-from-bottom-2 duration-200',
										activeTab === tab.id ? 'block' : 'hidden'
									)}
								>
									{tab.content}
								</div>
							))}
						</div>
					) : (
						<div className='p-4 sm:p-6'>
							{children}
						</div>
					)}
				</div>

				{/* Modal Footer */}
				<DialogFooter className={cn(
					'border-t bg-muted/20 px-4 py-3 sm:px-6 shrink-0',
					'flex-col sm:flex-row items-stretch sm:items-center gap-3'
				)}>
					{/* Status & Shortcuts */}
					<div className='hidden sm:flex items-center gap-4 flex-1'>
						<div className='text-xs text-muted-foreground'>
							{hasUnsavedChanges ? (
								<span className='inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-500'>
									<AlertCircle className='h-3.5 w-3.5' />
									Thay đổi chưa được lưu
								</span>
							) : (
								<span className='inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-500'>
									<CheckCircle2 className='h-3.5 w-3.5' />
									Đã lưu
								</span>
							)}
						</div>
						{tabs && tabs.length > 1 && (
							<div className='rounded-md bg-background/80 px-2 py-1 text-[11px] text-muted-foreground'>
								Tab {Math.max(activeTabIndex + 1, 1)}/{tabs.length}
							</div>
						)}
						<div className='hidden md:flex items-center gap-2'>
							<kbd className='inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold bg-muted rounded border border-border/50 text-muted-foreground'>
								{saveShortcut.includes('⌘') ? '⌘' : 'Ctrl'}
							</kbd>
							<kbd className='inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold bg-muted rounded border border-border/50 text-muted-foreground'>
								S
							</kbd>
						</div>
					</div>

					{/* Action Buttons */}
					<div className='flex items-center gap-2 sm:justify-end'>
						{onCancel && (
							<Button
								theme='secondary'
								preset='outline'
								onClick={onCancel}
								isDisabled={isSaving || isLoading}
								text='Hủy bỏ'
								sizeClass='sm'
							/>
						)}
						{onSave && (
							<Button
								theme='primary'
								preset='default'
								onClick={onSave}
								isLoading={isSaving}
								isDisabled={isSaving || isLoading}
								text={isSaving ? 'Đang lưu...' : 'Lưu thay đổi'}
								sizeClass='sm'
								icon='icon ni ni-check-thick'
							/>
						)}
						{footerActions}
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default EditModal;
